import React, { useEffect, useState } from 'react';
import axios from 'axios';
import DocumentTypeForm from './DocumentTypeForm';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import '../../../shared/ListRowHover.css';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { ChevronRight } from 'lucide-react';

const LIFECYCLE_TH = { textAlign: 'left', padding: '4px 6px', color: '#5a6b85', fontWeight: 600 };
const statusLabel = (status) => `${status.name} (${status.code})`;

// The kind's lifecycle rules (issue #277), read-only — maintained by the
// Optim team and the same for every tenant, served from the same rules the
// app checks every status move against (backend
// config/transactionLifecycles.js).
// Editable: what can change at each status, one column per entry in the
// lifecycle's `edit_columns` — a Sales Order shows an order raised
// directly and one converted from a Quote, a Quote shows one.
function LifecyclePanel({ lifecycle }) {
  if (!lifecycle) {
    return <div style={{ fontSize: 13, color: '#888' }}>Not on the lifecycle rules yet.</div>;
  }
  const editColumns = lifecycle.edit_columns || [];
  const hasEdit = editColumns.length > 0;
  return (
    <>
    <div style={{ fontSize: 13, color: '#5a6b85', marginBottom: 6 }}>
      {lifecycle.per === 'line'
        ? `Each line on a ${lifecycle.name} holds its own status.`
        : lifecycle.has_lines === false
          ? `The status belongs to the whole ${lifecycle.name}.`
          : `The status belongs to the whole ${lifecycle.name}; its lines have no status of their own.`}
    </div>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' }}>
      <thead>
        <tr>
          <th style={{ ...LIFECYCLE_TH, width: 70 }}>Code</th>
          <th style={{ ...LIFECYCLE_TH, width: hasEdit ? '12%' : '20%' }}>Status</th>
          <th style={{ ...LIFECYCLE_TH, width: hasEdit ? '12%' : '20%' }}>Next</th>
          <th style={LIFECYCLE_TH}>Other Allowed</th>
          {editColumns.map((col) => <th key={col.key} style={LIFECYCLE_TH}>{col.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {lifecycle.statuses.map((status) => (
          <tr key={status.key} style={{ borderTop: '1px solid #e3e7ee' }}>
            <td style={{ padding: '4px 6px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{status.code}</td>
            <td style={{ padding: '4px 6px' }}>{status.name}</td>
            <td style={{ padding: '4px 6px' }}>{status.next ? statusLabel(status.next) : '—'}</td>
            <td style={{ padding: '4px 6px' }}>{status.other_allowed.length ? status.other_allowed.map(statusLabel).join(', ') : '—'}</td>
            {editColumns.map((col) => (
              <td key={col.key} style={{ padding: '4px 6px', verticalAlign: 'top' }}>{status.edit?.[col.key]?.note || '—'}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
    </>
  );
}

const PANEL_HEADING_STYLE = { fontSize: 13, fontWeight: 600, color: '#222', margin: '12px 16px 6px 48px' };

// One row per kind of transaction (issue #277) — Optim ensures each one, so
// there's nothing to add or archive; Edit changes its code, description and
// next number. Expanding a row shows the kind's lifecycle.
function DocumentTypeTable() {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [editItem, setEditItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, []);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/document-types');
      setRows(res.data.data || []);
    } catch (err) {
      setRows([]);
    }
    setLoading(false);
  };

  const handleSave = async (data) => {
    setFormLoading(true);
    try {
      await axios.put(`/api/document-types/${editItem.id}`, data);
      setEditItem(null);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setFormLoading(false);
  };

  const th = (textAlign) => ({ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign });

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Document Types</h2>
        <p style={{ margin: '4px 0 0', color: '#888', fontSize: 12 }}>
          One per kind of transaction. Edit sets its code, description and next number. Expand a row to see the
          statuses it moves through, maintained by the Optim team.
        </p>
      </div>

      {editItem && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <DocumentTypeForm
            key={editItem.id}
            initial={editItem}
            onSave={handleSave}
            onCancel={() => setEditItem(null)}
            loading={formLoading}
          />
        </div>
      )}

      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '5%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '40%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '9%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 4px', fontWeight: 600, borderBottom: '1px solid #b3bbc7' }}></th>
              <th style={th('left')}>Type</th>
              <th style={th('left')}>Code</th>
              <th style={th('left')}>Description</th>
              <th style={th('right')}>Next Number</th>
              <th style={th('center')}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : rows.map((item) => (
              <React.Fragment key={item.id}>
                <tr
                  className="list-row"
                  onClick={() => setExpandedId((cur) => (cur === item.id ? null : item.id))}
                  style={{ cursor: 'pointer' }}
                  title="Show lifecycle"
                >
                  <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                    <ChevronRight size={16} strokeWidth={2} style={{ color: '#9aa5b5', transform: expandedId === item.id ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                  </td>
                  <td style={{ padding: '6px 8px', fontSize: 14 }}>{/* Each kind's name comes with its lifecycle (GET /api/document-types). */}{item.lifecycle?.name || item.type}</td>
                  <td style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600 }}>{item.code}</td>
                  <td
                    style={{ padding: '6px 8px', fontSize: 13, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={item.description || ''}
                  >
                    {item.description || '—'}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: 14 }}>{item.next_number}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <RowActionsMenu
                      items={[
                        { key: 'edit', label: 'Edit', disabled: !!editItem, disabledTitle: editItem ? 'Finish editing first' : undefined, onClick: () => setEditItem(item) },
                      ]}
                    />
                  </td>
                </tr>
                {expandedId === item.id && (
                  <tr>
                    <td colSpan={6} style={{ padding: 0, background: '#f7f9fc' }}>
                      <div style={PANEL_HEADING_STYLE}>Lifecycle</div>
                      <div style={{ padding: '0 16px 16px 48px' }}>
                        <LifecyclePanel lifecycle={item.lifecycle} />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default DocumentTypeTable;
